// ── Pay Profile Assignment Guard ──
// Wraps canAssignContractor with project-type → spec archetype mapping
// and contractor lookup-by-name (legacy: Job.assignedSubcontractor stores
// company name, not contractor id).
//
// Usage at every site that writes Job.assignedSubcontractor:
//
//   const check = assertContractorAssignable({
//     contractor, projectType: job.projectType, payProfiles,
//   });
//   if (!check.allowed) {
//     toast.error("Contractor Pay Profile required before assignment.", {
//       description: check.reason,
//     });
//     return;
//   }

import type { Contractor } from "./types";
import {
  type ContractorPayProfile,
  type SpecArchetype,
  canAssignContractor,
} from "./contractorPayProfile";

/**
 * Map Job.projectType → SpecArchetype.
 * Returns undefined when the project type is outside the spec system —
 * those jobs pass through with profile-existence-only enforcement
 * (no archetype check).
 */
export function projectTypeToArchetype(projectType?: string): SpecArchetype | undefined {
  switch (projectType) {
    case "Tub to Shower Conversion": return "TTS-STD";
    case "Tub to Shower Conversion (Wet Space Only)": return "WET-SPACE";
    case "Shower Replacement":
    case "Shower Remodel": return "SHWR-RPL";
    case "Shower Remodel (Wet Space Only)": return "WET-SPACE";
    case "Tub Replacement": return "TUB-RPL";
    case "Bathtub Remodel": return "TUB-RPL";
    case "Bathtub Remodel (Wet Space Only)": return "WET-SPACE";
    case "Walk-in Tub Installation": return "WALK-IN-TUB";
    case "Full Bathroom Remodel": return "FULL-BATH";
    case "Wet Space Only": return "WET-SPACE";
    case "Flooring": return "FLOORING";
    default: return undefined;
  }
}

export interface AssignmentGuardResult {
  allowed: boolean;
  reason: string;
  /** Plain-language toast title to show on block. */
  blockTitle: string;
  /** Plain-language toast description to show on block. */
  blockDescription: string;
}

const BLOCK_TITLE = "Contractor Pay Profile required before assignment.";
const BLOCK_DESC_GENERIC =
  "This contractor must have an active Pay Profile allowing this Job Spec.";

/**
 * Assignment gate. Resolves contractor to its id, then defers to
 * canAssignContractor. Pass `contractor` directly when known, otherwise
 * pass `contractorName` and `contractors` for legacy company-name writes.
 */
export function assertContractorAssignable(args: {
  contractor?: Contractor;
  contractorName?: string;
  contractors?: Contractor[];
  projectType?: string;
  payProfiles: ContractorPayProfile[];
}): AssignmentGuardResult {
  const { contractor, contractorName, contractors, projectType, payProfiles } = args;

  let resolved: Contractor | undefined = contractor;
  if (!resolved && contractorName && contractors) {
    resolved = contractors.find(
      (c) => c.companyName.trim().toLowerCase() === contractorName.trim().toLowerCase(),
    );
  }

  if (!resolved) {
    // Unresolvable name — likely an internal crew label, not a contractor.
    // Caller is responsible for routing crew assignments around this guard.
    return {
      allowed: true,
      reason: "Not a managed contractor (internal crew or unresolved name).",
      blockTitle: BLOCK_TITLE,
      blockDescription: BLOCK_DESC_GENERIC,
    };
  }

  const archetype = projectTypeToArchetype(projectType);
  const verdict = canAssignContractor(payProfiles, resolved.id, archetype);

  return {
    allowed: verdict.allowed,
    reason: verdict.reason,
    blockTitle: BLOCK_TITLE,
    blockDescription: verdict.reason,
  };
}
